/**
 * Main Screen - Entry Point
 * First screen in the verification flow with SafePassage branding
 */

import { useScreenNavigation } from '../../hooks';
import {
  analyticsService,
  JourneyEventType,
} from '../../services/AnalyticsService';
import { NeutralSpinner, VerificationScreenTemplate } from '../shared';
import { runtimeConfig } from '../../services/RuntimeConfigService';
import { logger } from '../../utils/logger';
import { EmblemInfoModal } from './EmblemInfoModal';
import { featureFlagService } from '../../services/FeatureFlagService';
import { sessionReportingService } from '../../services/SessionReportingService';
import { useState, useEffect, useRef } from 'react';
import {
  getStoredSkipFlowParams,
  parseSkipFlowParams,
  canSkipIntro,
  clearSkipFlowParams,
} from '../../utils/skipFlowParams';
import {
  showErrorNotification,
  showInfoNotification,
} from '../../utils/userNotifications';
import { resolveFlowMode } from '../../utils/newTabHandler';
import { markIntentionalNavigation } from '../../utils/navigationIntent';
import { tUnsafe, useLanguage } from '../../i18n';

const mainScreenLogger = logger.clone({ labels: { component: 'MainScreen' }});

export function canInitiateEmblemAssertion(sessionStatus?: string): boolean {
  return !sessionStatus || sessionStatus === 'PENDING';
}

export function MainScreen() {
  const { navigateToScreen } = useScreenNavigation();
  const { t } = useLanguage();
  const [emblemEnabled, setEmblemEnabled] = useState(false);
  const [emblemAssertionAllowed, setEmblemAssertionAllowed] = useState(false);
  const navigateToScreenRef = useRef(navigateToScreen);
  // One Emblem launch per visit to this screen: a second click while the first
  // is still leaving the page would start a second Emblem transaction.
  const emblemLaunchInFlightRef = useRef(false);
  const [skipIntroPending, setSkipIntroPending] = useState(() => {
    try {
      const stored = getStoredSkipFlowParams();
      const parsed = parseSkipFlowParams();
      return stored?.skipIntro ?? parsed.skipIntro ?? false;
    } catch {
      return false;
    }
  });

  // Returning with Back restores this page from the back/forward cache with the
  // launch still marked in flight; allow a new launch then.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        emblemLaunchInFlightRef.current = false;
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Clear any stale QR flags at the start of a new flow
  useEffect(() => {
    try {
      sessionStorage.removeItem('auto_show_qr');
    } catch {
      // Ignore storage cleanup failures to keep flow resilient in restricted browsers
    }
  }, []);

  useEffect(() => {
    navigateToScreenRef.current = navigateToScreen;
  }, [navigateToScreen]);

  // Fetch feature flags
  useEffect(() => {
    let cancelled = false;

    const initializeMainScreen = async () => {
      try {
        const enabled = await featureFlagService.isEmblemEnabled();
        if (cancelled) {
          return;
        }

        setEmblemEnabled(enabled);

        const config = runtimeConfig.isLoaded()
          ? runtimeConfig.getConfig()
          : await runtimeConfig.loadConfig();
        const sessionId = config.sessionId;

        if (!sessionId) {
          setEmblemAssertionAllowed(enabled);
          return;
        }

        const sessionStatus = await sessionReportingService.getSessionStatus(sessionId);
        if (cancelled) {
          return;
        }

        setEmblemAssertionAllowed(
          enabled && canInitiateEmblemAssertion(sessionStatus.status)
        );

        if (sessionStatus.verified) {
          mainScreenLogger.info('Redirecting verified session away from main screen', {
            labels: {
              sessionId,
              sessionStatus: sessionStatus.status || 'unknown',
            },
          });
          navigateToScreenRef.current('success');
        }
      } catch (error) {
        mainScreenLogger.warn('Failed to load feature flags, defaulting to disabled', {
          error: error instanceof Error ? error : new Error(`${error}`),
        });
        setEmblemEnabled(false);
        setEmblemAssertionAllowed(false);
      }
    };

    void initializeMainScreen();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveSkipIntro = async () => {
      let requested = false;
      try {
        const stored = getStoredSkipFlowParams();
        const parsed = parseSkipFlowParams();
        requested = stored?.skipIntro ?? parsed.skipIntro ?? false;
      } catch {
        requested = false;
      }

      if (!requested) {
        setSkipIntroPending(false);
        return;
      }

      setSkipIntroPending(true);

      let can = canSkipIntro();
      if (!can.ok && can.reason === 'config_unavailable') {
        try {
          await runtimeConfig.loadConfig();
        } catch {
          // Ignore config load failures and fall back to the standard check.
        }
        can = canSkipIntro();
      }

      if (cancelled) return;

      if (can.ok) {
        clearSkipFlowParams(['skipIntro']);
        navigateToScreenRef.current('camera-access');
        return;
      }

      setSkipIntroPending(false);
      showErrorNotification(
        { key: 'mainScreen.missingSession' },
        { title: tUnsafe('common.notice') }
      );
    };

    void resolveSkipIntro();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      const blocked = sessionStorage.getItem('sp_direct_access_blocked');
      if (blocked === 'true') {
        sessionStorage.removeItem('sp_direct_access_blocked');
        showErrorNotification(
          { key: 'mainScreen.directAccessBlocked' },
          { title: tUnsafe('common.notice') }
        );
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const resumeReason = url.searchParams.get('emblem_resume');

    if (!resumeReason) {
      return;
    }

    // Authentication succeeded but no reusable pass exists: that is not a
    // failed sign-in, so it gets its own explanation.
    const noticeKey =
      resumeReason === 'no_reusable_record'
        ? 'mainScreen.emblemNoReusableNotice'
        : 'mainScreen.emblemResumeNotice';
    showInfoNotification(tUnsafe(noticeKey), {
      title: tUnsafe('common.notice'),
    });

    url.searchParams.delete('emblem_resume');
    window.history.replaceState({}, '', url.toString());
  }, []);

  if (skipIntroPending) {
    return <NeutralSpinner />;
  }

  const handleStartVerification = () => {
    // Track flow start event
    analyticsService.trackEvent(JourneyEventType.VERIFICATION_FLOW_STARTED, {
      source: 'main_screen',
      buttonClicked: 'verify_now',
    });

    void sessionReportingService.markVerificationStarted();

    // Skip loading screen - go directly to camera-access
    navigateToScreen('camera-access');
  };

  const handleEmblemLogin = async () => {
    if (emblemLaunchInFlightRef.current) {
      return;
    }
    emblemLaunchInFlightRef.current = true;
    try {
      // Track Emblem login event
      analyticsService.trackEvent(JourneyEventType.VERIFICATION_FLOW_STARTED, {
        source: 'main_screen',
        buttonClicked: 'login_with_emblem',
        verificationType: 'emblem',
      });

      // Get configuration
      const config = runtimeConfig.getConfig();
      const apiUrl = config.apiUrl;

      const merchantId = config.apiKey;
      const returnUrl = config.returnUrl;
      const cancelUrl = config.cancelUrl;
      const verificationMode = config.verificationMode;
      const sessionId = config.sessionId;

      if (!merchantId || !sessionId || !returnUrl) {
        mainScreenLogger.error('Authoritative session configuration is incomplete');
        emblemLaunchInFlightRef.current = false;
        return;
      }

      // Get the current flow mode (new-tab or redirect)
      const flowMode = resolveFlowMode();

      // Build Emblem login URL with mode for proper callback handling.
      // cancelUrl is optional server-side; only include it when the merchant
      // supplied one. Falling back to window.location.href can push the URL
      // past validator.js's 2084-char isURL limit because the current page URL
      // embeds sessionToken/state, causing a 400 "cancelUrl must be a URL
      // address".
      const emblemParams = new URLSearchParams({
        apiKey: merchantId,
        verificationMode: verificationMode || 'L1',
        returnUrl,
        mode: flowMode,
      });
      if (cancelUrl) {
        emblemParams.set('cancelUrl', cancelUrl);
      }
      if (sessionId) {
        emblemParams.set('sessionId', sessionId);
      }

      const emblemLoginUrl = `${apiUrl}/api/v1/auth/emblem/v2/assert?${emblemParams}`;

      mainScreenLogger.info('Redirecting to Emblem login', {
        labels: {
          apiUrl,
          verificationMode: verificationMode || 'L1',
        }
      });

      await sessionReportingService.markVerificationStarted(sessionId);
      markIntentionalNavigation('emblem-redirect', sessionId);

      // Redirect to Emblem login
      window.location.href = emblemLoginUrl;
    } catch (error) {
      emblemLaunchInFlightRef.current = false;
      mainScreenLogger.error('Error initiating Emblem login', error instanceof Error ? error : new Error(`${error}`));
      // Could show an error message to user here
    }
  };

  return (
    <VerificationScreenTemplate
      title={t('mainScreen.title')}
      description={t('mainScreen.description')}
      icon={{
        src: '/icons/green-check.svg',
        alt: t('mainScreen.iconAlt'),
        size: '5xl',
      }}
      primaryAction={{
        label: t('mainScreen.primaryAction'),
        onClick: handleStartVerification,
      }}
      secondaryAction={emblemEnabled && emblemAssertionAllowed ? {
        label: t('mainScreen.secondaryAction'),
        onClick: handleEmblemLogin,
        icon: '/icons/emblem_icon_light.svg',
        darkIcon: '/icons/emblem_icon_dark.svg',
      } : undefined}
      separatorText={emblemEnabled && emblemAssertionAllowed ? t('mainScreen.separatorText') : undefined}
      desktopOptimized={true}
      afterActions={emblemEnabled && emblemAssertionAllowed ? <EmblemInfoModal /> : undefined}
    />
  );
}
